import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Text,
} from "@/components/ui";
import { SelectDropdownChevron } from "@/components/ui/shadcn/Select/Select";
import { type Table } from "@tanstack/react-table";
import { sortBy } from "lodash";
import { Eye } from "lucide-react";
import { useState } from "react";
import { formatTableColumnLabel } from "./formatTableColumnLabel";
import type { DataTableColumnMeta } from "./DataTable";

type DataTableVisibilityDropdownProps<TData> =
  | { table: Table<TData>; columns?: never; onVisibilityChange?: never }
  | {
      table?: never;
      columns: { id: string; label: string; isVisible: boolean }[];
      onVisibilityChange: (columnIds: string[], visible: boolean) => void;
    };

export function DataTableVisibilityDropdown<TData>(
  props: DataTableVisibilityDropdownProps<TData>,
) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const hasSearchValue = searchValue.trim().length > 0;

  const hideableColumns = sortBy(
    props.table
      ? props.table
          .getAllColumns()
          .filter((column) => column.getCanHide())
          .map((column) => ({
            id: column.id,
            label: formatTableColumnLabel(
              column.id,
              (column.columnDef.meta as DataTableColumnMeta | undefined)
                ?.preserveHeaderLabel,
            ),
            isVisible: column.getIsVisible(),
          }))
      : props.columns,
    "label",
  );

  const normalizedSearchValue = searchValue.trim().toLowerCase();
  const filteredColumns = normalizedSearchValue
    ? hideableColumns.filter((column) =>
        column.label.toLowerCase().includes(normalizedSearchValue),
      )
    : hideableColumns;

  const visibleHideableColumnCount = hideableColumns.filter(
    (column) => column.isVisible,
  ).length;
  const areAllHideableColumnsVisible =
    hideableColumns.length > 0 &&
    visibleHideableColumnCount === hideableColumns.length;
  const visibleFilteredColumnCount = filteredColumns.filter(
    (column) => column.isVisible,
  ).length;
  const areAllFilteredColumnsVisible =
    filteredColumns.length > 0 &&
    visibleFilteredColumnCount === filteredColumns.length;

  const toggleableColumns = hasSearchValue ? filteredColumns : hideableColumns;
  const areAllToggleableColumnsVisible = hasSearchValue
    ? areAllFilteredColumnsVisible
    : areAllHideableColumnsVisible;
  const bulkToggleLabel = hasSearchValue ? "Toggle filtered" : "Toggle all";

  const updateColumnsVisibility = (nextVisibility: boolean) => {
    const ids = toggleableColumns.map((column) => column.id);
    if (props.table) {
      props.table.setColumnVisibility((currentVisibility) => ({
        ...currentVisibility,
        ...Object.fromEntries(ids.map((id) => [id, nextVisibility])),
      }));
    } else {
      props.onVisibilityChange(ids, nextVisibility);
    }
  };

  return (
    <DropdownMenu
      modal={false}
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setSearchValue("");
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          id="data-table-visibility-dropdown-trigger"
          className="group h-9 shrink-0 gap-2 px-3"
        >
          <Eye className="h-4 w-4 shrink-0" />
          <Text as="span" variant="body" className="hidden md:block">
            Columns
          </Text>
          <SelectDropdownChevron />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="flex h-96 w-72 flex-col p-0">
        <div className="p-2">
          <Input
            autoFocus
            value={searchValue}
            placeholder="Search columns..."
            data-testid="data-table-visibility-search"
            onChange={(event) => {
              setSearchValue(event.target.value);
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
          />
        </div>
        <DropdownMenuSeparator className="my-0" />
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1">
          <DropdownMenuCheckboxItem
            className="bg-muted/40 mb-1 rounded-md font-semibold"
            data-testid="data-table-visibility-toggle-all"
            checked={areAllToggleableColumnsVisible}
            disabled={toggleableColumns.length === 0}
            onSelect={(event) => {
              event.preventDefault();
            }}
            onCheckedChange={(value) => {
              updateColumnsVisibility(!!value);
            }}
          >
            {bulkToggleLabel}
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator className="bg-border/80 my-2 h-0.5" />
          {filteredColumns.length > 0 ? (
            filteredColumns.map((column) => {
              return (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  className="rounded-md"
                  checked={column.isVisible}
                  onSelect={(event) => {
                    event.preventDefault();
                  }}
                  onCheckedChange={(value) => {
                    if (props.table) {
                      props.table
                        .getColumn(column.id)
                        ?.toggleVisibility(!!value);
                    } else {
                      props.onVisibilityChange([column.id], !!value);
                    }
                  }}
                >
                  {column.label}
                </DropdownMenuCheckboxItem>
              );
            })
          ) : (
            <Text variant="subHeading" className="px-2 py-3">
              No columns found.
            </Text>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
